import React, { Component } from 'react';
import {
    Dialog, DialogContent, Typography, Grid, Divider, IconButton, Tooltip,
    CircularProgress, Chip, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SectionCard from '../section-card';
import { ContractStatusBadge, ProvisioningStatusBadge } from '../status-badge';
import ForceDropConfirmDialog from '../force-drop-confirm-dialog';
import CustomizedSnackbars from '../material-snackbars';
import * as adminBillingService from '../../services/admin-billing.service';
import Api from '../../services/api';

// Rótulos em pt-BR dos action_types gravados pelo backend.
const ACTION_LABELS = {
    'deprovision_started': 'Desprovisionamento iniciado',
    'deprovision_forced_by_admin': 'Desprovisionamento forçado pelo admin',
    'drop_forced_by_admin': 'Drop do banco forçado pelo admin',
    'deprov_update_status': 'Status atualizado para "deprovisioning"',
    'deprov_teardown_k8s': 'Teardown da infraestrutura K8s',
    'deprov_cancel_gateway': 'Assinatura cancelada no gateway',
    'deprov_soft_delete': 'Soft-delete das entidades SaaS',
    'deprov_enqueue_drop': 'Drop do banco enfileirado',
    'deprov_publish_drop_event': 'Evento de drop publicado (forçado)',
    'deprov_complete': 'Desprovisionamento concluído',
    'deprov_fail': 'Falha no desprovisionamento',
    'deprov_drop_database': 'DROP DATABASE executado',
    'deprov_drop_database_user': 'DROP USER executado',
    'deprov_drop_database_skipped': 'Drop pulado (outro contrato ativo)',
    'deprov_drop_complete': 'Drop finalizado',
    'deprov_drop_fail': 'Falha no drop',
    'deprovision_retry': 'Retry manual do desprovisionamento',
    'force_cleanup_k8s_requested': 'Force-cleanup K8s solicitado pelo admin',
    'force_cleanup_k8s_started': 'Force-cleanup K8s iniciado',
    'force_cleanup_k8s_complete': 'Force-cleanup K8s concluído',
    'force_cleanup_k8s_failed': 'Force-cleanup K8s falhou',
};

// Linhas force_cleanup_k8s_<kind>_<name> são geradas dinamicamente (uma por delete).
// Dão match no fallback default da tabela e ficam com o action_type bruto — esperado.


class DeprovisionedTrialDetailDialog extends Component {
    state = {
        loading: false,
        detail: null,
        history: [],
        forceDropOpen: false,
        forceDropSending: false,
        forceDropSubmitting: false,
        forceDropCodeSentState: null,
        forceCleanupSubmitting: false,
        confirmDialog: { open: false, title: '', message: '', action: null },
        snackbar: { open: false, variant: 'success', message: '' },
    };

    openConfirm = (title, message, action) => {
        this.setState({ confirmDialog: { open: true, title, message, action } });
    }

    closeConfirm = () => {
        this.setState({ confirmDialog: { open: false, title: '', message: '', action: null } });
    }

    openSnackbar = (variant, message) => {
        // Padrão do projeto: CustomizedSnackbars com variant 'success'/'error'.
        this.setState({ snackbar: { open: true, variant, message } });
    }

    closeSnackbar = () => {
        this.setState({ snackbar: { open: false, variant: 'success', message: '' } });
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open && this.props.desprovisioningQueueId) {
            this.load();
        }
        if (!this.props.open && prevProps.open) {
            this.setState({ detail: null, history: [], forceDropOpen: false, forceDropCodeSentState: null });
        }
    }

    load = () => {
        this.setState({ loading: true });
        adminBillingService.getDeprovisionedTrialDetail(this.props.desprovisioningQueueId)
            .then(result => {
                if (result.data.success) {
                    const detail = result.data.data;
                    this.setState({ loading: false, detail });
                    if (detail?.provisioningqueueid > 0) {
                        this.loadHistory(detail.provisioningqueueid);
                    }
                    // Avisa o parent (lista) pra atualizar banner/linha também.
                    if (this.props.onDataRefreshed) this.props.onDataRefreshed();
                } else {
                    this.setState({ loading: false, detail: null });
                }
            })
            .catch(err => { this.setState({ loading: false, detail: null }); Api.kickoff(err); });
    }

    loadHistory = (provisioningQueueId) => {
        adminBillingService.getProvisioningDetail(provisioningQueueId)
            .then(result => {
                if (result.data.success && result.data.data) {
                    this.setState({ history: result.data.data.history || [] });
                }
            })
            .catch(() => {});
    }

    formatDate = (iso) => iso ? new Date(iso).toLocaleString('pt-BR') : '—';

    daysUntilDrop = () => {
        const d = this.state.detail;
        if (!d?.dropscheduledat) return null;
        const diffMs = new Date(d.dropscheduledat).getTime() - Date.now();
        if (diffMs <= 0) return 0;
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }

    renderDropStatusChip = (status) => {
        if (!status) return <span>—</span>;
        const map = {
            pending: { label: 'Pendente', bg: '#fff9c4', color: '#f57f17' },
            processing: { label: 'Processando', bg: '#90caf9', color: '#0d47a1' },
            dropped: { label: 'Deletado', bg: '#616161', color: '#fafafa' },
            failed: { label: 'Falhou', bg: '#ef9a9a', color: '#b71c1c' },
            aborted: { label: 'Abortado', bg: '#e0e0e0', color: '#424242' },
        };
        const s = map[status] || { label: status, bg: '#e0e0e0', color: '#424242' };
        return <Chip size="small" label={s.label} style={{ backgroundColor: s.bg, color: s.color, fontWeight: 500 }} />;
    }

    renderField = (label, value, opts = {}) => (
        <Grid item xs={12} sm={opts.full ? 12 : 6}>
            <Typography variant="body2" color="textSecondary">{label}</Typography>
            <Typography variant="body1" style={{ fontWeight: opts.bold ? 500 : 400, fontFamily: opts.mono ? 'monospace' : undefined }}>
                {value ?? '—'}
            </Typography>
        </Grid>
    );

    // Force-cleanup K8s (item-a-item por nome) pra contrato JÁ desprovisionado cujo
    // teardown deixou recursos órfãos no cluster. Endpoint é assíncrono (202 Accepted);
    // o trabalho roda no handler RabbitMQ e o progresso aparece no histórico via polling.
    handleForceK8sCleanup = () => {
        const { detail } = this.state;
        if (!detail?.provisioningqueueid || this.state.forceCleanupSubmitting) return;

        this.openConfirm(
            'Limpar K8s',
            'Esta operação dispara cleanup ASSÍNCRONO no cluster Kubernetes para este contrato JÁ desprovisionado. ' +
            'A requisição é validada e enfileirada (resposta imediata 202); o trabalho roda no handler do RabbitMQ ' +
            'e o progresso aparece no histórico (linhas force_cleanup_k8s_*). ' +
            'NÃO toca em banco, contrato, hirer ou queue — apenas K8s, item-a-item por nome do que foi criado. ' +
            'Deseja continuar?',
            () => this.runForceK8sCleanup()
        );
    }

    runForceK8sCleanup = () => {
        const { detail } = this.state;
        if (!detail?.provisioningqueueid) return;

        this.setState({ forceCleanupSubmitting: true, confirmDialog: { open: false, title: '', message: '', action: null } });
        adminBillingService.forceK8sCleanup(detail.provisioningqueueid)
            .then(result => {
                this.setState({ forceCleanupSubmitting: false });
                // Tanto 200 quanto 202 entram aqui (axios resolve qualquer 2xx).
                // Service responde 202 + { success: true, data: { ... } } no caminho feliz,
                // ou CustomResponse() com errors quando rejeita.
                if (result?.data?.success) {
                    this.openSnackbar('success', 'Limpeza K8s enfileirada — acompanhe o progresso no histórico');
                    // Recarrega detail+history pra mostrar a linha 'force_cleanup_k8s_requested'.
                    setTimeout(() => this.load(), 800);
                } else {
                    const err = result?.data?.errors?.[0] || 'Falha ao solicitar limpeza K8s';
                    this.openSnackbar('error', err);
                }
            })
            .catch(err => {
                this.setState({ forceCleanupSubmitting: false });
                const msg = err?.response?.data?.errors?.[0] || 'Falha ao solicitar limpeza K8s';
                this.openSnackbar('error', msg);
                Api.kickoff(err);
            });
    }

    // Fluxo 2FA (igual ao admin-trial-detail/admin-subscription-detail).
    // ForceDropConfirmDialog chama onSendCode({ email }), não o email direto.
    handleSendForceDropCode = ({ email }) => {
        const { detail } = this.state;
        if (!detail?.hirerid) return;
        this.setState({ forceDropSending: true });
        adminBillingService.sendForceDropTwoFactorCode(detail.hirerid, email)
            .then(result => {
                if (result.data.success) {
                    this.setState({ forceDropSending: false, forceDropCodeSentState: { ...result.data.data, sent: true } });
                } else {
                    this.setState({ forceDropSending: false, forceDropCodeSentState: { sent: false, errormessage: result.data.errors?.[0] || 'Falha ao enviar código' } });
                }
            })
            .catch(err => { this.setState({ forceDropSending: false, forceDropCodeSentState: { sent: false, errormessage: 'Falha ao enviar código' } }); Api.kickoff(err); });
    }

    handleConfirmForceDrop = (payload) => {
        const { detail } = this.state;
        if (!detail?.hirerid || !detail?.contractid) return;
        this.setState({ forceDropSubmitting: true, forceDropError: null });
        adminBillingService.forceDatabaseDrop(detail.hirerid, detail.contractid, payload)
            .then(result => {
                if (result.data.success) {
                    this.setState({
                        forceDropSubmitting: false,
                        forceDropOpen: false,
                        forceDropCodeSentState: null,
                        forceDropError: null,
                    });
                    // Recarrega o detail + avisa parent pra refletir na lista.
                    setTimeout(() => this.load(), 800);
                    if (this.props.onForceDropSuccess) this.props.onForceDropSuccess();
                } else {
                    // Mantém modal aberto pra usuário corrigir
                    this.setState({
                        forceDropSubmitting: false,
                        forceDropError: result.data.errors?.[0] || 'Falha ao forçar drop'
                    });
                }
            })
            .catch(err => {
                this.setState({
                    forceDropSubmitting: false,
                    forceDropError: err.response?.data?.errors?.[0] || 'Falha ao forçar drop'
                });
                Api.kickoff(err);
            });
    }

    render() {
        const { open, onClose } = this.props;
        const { loading, detail, history } = this.state;

        const daysLeft = this.daysUntilDrop();
        const dropPending = detail?.dropstatus === 'pending';
        const dropDone = detail?.dropstatus === 'dropped';
        const dropFailed = detail?.dropstatus === 'failed';

        return (
            <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#fafafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ArchiveIcon style={{ color: '#616161' }} />
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>Detalhes do ambiente desprovisionado</Typography>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Tooltip title="Atualizar dados">
                            <span>
                                <IconButton size="small" onClick={this.load} disabled={loading} style={{ color: '#616161' }}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton size="small" onClick={onClose} style={{ color: '#9e9e9e' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                </div>
                <Divider />

                <DialogContent style={{ padding: 24, backgroundColor: '#f5f7fa', maxHeight: '80vh', overflowY: 'auto' }}>
                    {loading && (
                        <div style={{ textAlign: 'center', padding: 48 }}>
                            <CircularProgress />
                        </div>
                    )}

                    {!loading && !detail && (
                        <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 32 }}>
                            Não foi possível carregar os detalhes.
                        </Typography>
                    )}

                    {!loading && detail && (
                        <Grid container spacing={2}>
                            {/* DESTAQUE: Retenção do banco */}
                            <Grid item xs={12}>
                                <SectionCard
                                    context={dropDone ? 'success' : dropFailed ? 'danger' : 'warning'}
                                    title="Retenção do banco"
                                    actions={
                                        dropPending && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<DeleteForeverIcon />}
                                                onClick={() => this.setState({ forceDropOpen: true, forceDropCodeSentState: null, forceDropError: null })}
                                                style={{ backgroundColor: '#c62828', color: '#fff', textTransform: 'none', fontWeight: 600 }}
                                            >
                                                Forçar drop agora
                                            </Button>
                                        )
                                    }
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2" color="textSecondary">Estado do banco</Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                {dropDone ? (
                                                    <>
                                                        <CheckCircleIcon style={{ color: '#388e3c' }} />
                                                        <Chip label="Banco deletado" size="small" style={{ backgroundColor: '#c8e6c9', color: '#1b5e20', fontWeight: 600 }} />
                                                    </>
                                                ) : dropFailed ? (
                                                    <>
                                                        <ErrorIcon style={{ color: '#c62828' }} />
                                                        <Chip label="Falha no drop" size="small" style={{ backgroundColor: '#ffcdd2', color: '#b71c1c', fontWeight: 600 }} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <ScheduleIcon style={{ color: '#ef6c00' }} />
                                                        <Chip label="Banco ainda existe" size="small" style={{ backgroundColor: '#ffe0b2', color: '#e65100', fontWeight: 600 }} />
                                                    </>
                                                )}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2" color="textSecondary">Status do drop</Typography>
                                            <div style={{ marginTop: 4 }}>{this.renderDropStatusChip(detail.dropstatus)}</div>
                                        </Grid>
                                        {dropPending && daysLeft != null && (
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="body2" color="textSecondary">Dias até o drop</Typography>
                                                <Typography variant="h5" style={{ fontWeight: 700, color: daysLeft <= 3 ? '#c62828' : '#ef6c00' }}>
                                                    {daysLeft}{' '}<span style={{ fontSize: 14, fontWeight: 500, color: '#616161' }}>{daysLeft === 1 ? 'dia' : 'dias'}</span>
                                                </Typography>
                                            </Grid>
                                        )}
                                        {this.renderField('SaaS arquivado em', this.formatDate(detail.archivedat))}
                                        {this.renderField('Drop agendado para', this.formatDate(detail.dropscheduledat))}
                                        {this.renderField('Banco dropado em', this.formatDate(detail.droppedat))}
                                        {detail.droperrormessage && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="textSecondary">Erro no drop</Typography>
                                                <Typography variant="body2" style={{ color: '#b71c1c', backgroundColor: '#ffebee', padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>
                                                    {detail.droperrormessage}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </SectionCard>
                            </Grid>

                            {/* Contratante */}
                            <Grid item xs={12}>
                                <SectionCard context="info" title="Dados do contratante">
                                    <Grid container spacing={2}>
                                        {this.renderField('Nome', detail.name, { bold: true })}
                                        {this.renderField('Workspace', detail.workspace, { mono: true, bold: true })}
                                        {this.renderField('Nome do responsável', detail.responsiblename)}
                                        {this.renderField('Email do responsável', detail.responsiblemail)}
                                        {this.renderField('Telefone do responsável', detail.responsiblephone)}
                                    </Grid>
                                </SectionCard>
                            </Grid>

                            {/* Contrato */}
                            <Grid item xs={12}>
                                <SectionCard context="info" title="Dados do contrato">
                                    <Grid container spacing={2}>
                                        {this.renderField('Plano', detail.planname)}
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Status do contrato</Typography>
                                            <div style={{ marginTop: 2 }}><ContractStatusBadge statusId={detail.contractstatusid} /></div>
                                        </Grid>
                                        {this.renderField('Data de início', this.formatDate(detail.startdate))}
                                        {this.renderField('Trial end date', this.formatDate(detail.trialenddate))}
                                        {this.renderField('Hirer ID', detail.hirerid, { mono: true })}
                                        {this.renderField('Contract ID', detail.contractid, { mono: true })}
                                    </Grid>
                                </SectionCard>
                            </Grid>

                            {/* Solicitação */}
                            <Grid item xs={12}>
                                <SectionCard context="warning" title="Solicitação do desprovisionamento">
                                    <Grid container spacing={2}>
                                        {this.renderField('Solicitado por', detail.deprovisioningrequestedby)}
                                        {this.renderField('Agendado para', this.formatDate(detail.deprovisioningscheduledat))}
                                        {this.renderField('Motivo', detail.deprovisioningreason || '—', { full: true })}
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Tipo</Typography>
                                            <Chip size="small" label={detail.contracttype || 'trial'} style={{ backgroundColor: '#e1f5fe', color: '#01579b', fontWeight: 500 }} />
                                        </Grid>
                                        {detail.forceduser != null && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="textSecondary">Forçado por (user_id)</Typography>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <Chip size="small" label="Forçado" style={{ backgroundColor: '#ffebee', color: '#b71c1c', fontWeight: 600 }} />
                                                        <Typography variant="body1" style={{ fontFamily: 'monospace' }}>{detail.forceduser}</Typography>
                                                    </div>
                                                </Grid>
                                                {this.renderField('Forçado em', this.formatDate(detail.forcedat))}
                                            </>
                                        )}
                                    </Grid>
                                </SectionCard>
                            </Grid>

                            {/* Histórico de etapas */}
                            <Grid item xs={12}>
                                <SectionCard context="history" title="Histórico de etapas" noPadding>
                                    {history.length === 0 ? (
                                        <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
                                            Nenhum histórico registrado.
                                        </Typography>
                                    ) : (
                                        <TableContainer component={Paper} variant="outlined" style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow style={{ backgroundColor: '#fafafa' }}>
                                                        <TableCell>Ação</TableCell>
                                                        <TableCell>Origem</TableCell>
                                                        <TableCell>Data</TableCell>
                                                        <TableCell align="center">Status</TableCell>
                                                        <TableCell>Erro</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {history.map(h => (
                                                        <TableRow key={h.id} hover style={h.rabbitmqerror ? { backgroundColor: '#fff5f5' } : undefined}>
                                                            <TableCell>{ACTION_LABELS[h.actiontype] || h.actiontype}</TableCell>
                                                            <TableCell style={{ color: '#616161', fontSize: 12 }}>{h.triggeredby || '-'}</TableCell>
                                                            <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>{this.formatDate(h.triggeredat)}</TableCell>
                                                            <TableCell align="center">
                                                                {h.processed && !h.rabbitmqerror
                                                                    ? <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
                                                                    : h.rabbitmqerror
                                                                        ? <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />
                                                                        : <ScheduleIcon style={{ color: '#9e9e9e', fontSize: 18 }} />}
                                                            </TableCell>
                                                            <TableCell style={{ fontSize: 12, color: '#b71c1c', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {h.rabbitmqerror ? (
                                                                    <Tooltip title={h.rabbitmqerror}><span>{h.rabbitmqerror}</span></Tooltip>
                                                                ) : '—'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </SectionCard>
                            </Grid>

                            {/* Técnico */}
                            <Grid item xs={12}>
                                <SectionCard
                                    context="provisioning"
                                    title="Status técnico do provisionamento"
                                    actions={
                                        // Só dedicated/conversion tem K8s pra limpar. Backend revalida tudo
                                        // (queue.status='deprovisioned', contract.status_id=9, hirer.deleted=true,
                                        // step 12 com generatedManifests, sem in-flight).
                                        detail?.provisioningqueueid > 0 &&
                                        (detail?.contracttype === 'dedicated' || detail?.contracttype === 'conversion') && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DeleteForeverIcon />}
                                                onClick={this.handleForceK8sCleanup}
                                                disabled={this.state.forceCleanupSubmitting}
                                                title="Cleanup K8s pra contrato já desprovisionado (recursos órfãos no cluster)"
                                                style={{ borderColor: '#c62828', color: '#c62828', textTransform: 'none' }}
                                            >
                                                Limpar K8s
                                            </Button>
                                        )
                                    }
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Status</Typography>
                                            <div style={{ marginTop: 2 }}>
                                                {detail.provisioningstatus
                                                    ? <ProvisioningStatusBadge status={detail.provisioningstatus} />
                                                    : <span>—</span>}
                                            </div>
                                        </Grid>
                                        {this.renderField('Step que falhou', detail.provisioningfailedstep)}
                                        {detail.provisioningerrormessage && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="textSecondary">Mensagem de erro</Typography>
                                                <Typography variant="body2" style={{ color: '#b71c1c', backgroundColor: '#ffebee', padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>
                                                    {detail.provisioningerrormessage}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </SectionCard>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>

            {/* Force drop confirm — reutiliza o mesmo componente de trial/subscription-detail */}
            {detail && (
                <ForceDropConfirmDialog
                    open={this.state.forceDropOpen}
                    onClose={() => this.setState({ forceDropOpen: false, forceDropCodeSentState: null, forceDropError: null })}
                    onSendCode={this.handleSendForceDropCode}
                    onConfirm={this.handleConfirmForceDrop}
                    workspace={detail.workspace}
                    defaultEmail={this.props.defaultEmail || ''}
                    sending={this.state.forceDropSending}
                    submitting={this.state.forceDropSubmitting}
                    codeSentState={this.state.forceDropCodeSentState}
                    submitError={this.state.forceDropError}
                />
            )}

            {/* Confirm dialog — mesmo padrão do provisioning-detail-panel */}
            <Dialog open={this.state.confirmDialog.open} onClose={this.closeConfirm} maxWidth="xs" fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#f5f7fa' }}>
                    <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>{this.state.confirmDialog.title}</Typography>
                    <IconButton size="small" onClick={this.closeConfirm} style={{ color: '#9e9e9e' }}><CloseIcon fontSize="small" /></IconButton>
                </div>
                <Divider />
                <DialogContent style={{ padding: '16px 24px' }}>
                    <Typography>{this.state.confirmDialog.message}</Typography>
                </DialogContent>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 24px 16px', backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={this.closeConfirm} style={{ textTransform: 'none', fontWeight: 500 }}>Cancelar</Button>
                    <Button variant="contained" color="primary" onClick={this.state.confirmDialog.action}
                        style={{ textTransform: 'none', fontWeight: 600, borderRadius: 6 }}>
                        Confirmar
                    </Button>
                </div>
            </Dialog>

            {this.state.snackbar.open && (
                <CustomizedSnackbars
                    isOpen={this.state.snackbar.open}
                    variant={this.state.snackbar.variant}
                    message={this.state.snackbar.message}
                    toClose={this.closeSnackbar}
                />
            )}
            </>
        );
    }
}

export default DeprovisionedTrialDetailDialog;
